<template>
  <div class="page">
    <!-- 顶部标题卡 -->
    <a-card class="toolbar-card" :bordered="false">
      <div class="toolbar">
        <div class="title-wrap">
          <div class="title">编辑员工</div>
          <div class="sub muted">修改员工信息并上传照片</div>
        </div>

        <div class="toolbar-actions">
          <a-button @click="goList" :disabled="submitting">返回列表</a-button>
        </div>
      </div>
    </a-card>

    <!-- 主表单卡 -->
    <a-card class="content-card" :bordered="false">
      <a-form ref="formRef" :model="form" layout="vertical" :hideRequiredMark="true">
        <!-- 两列布局 -->
        <a-row :gutter="24">
          <a-col :xs="24" :md="12">
            <a-form-item label="姓名">
              <a-input v-model="form.name" disabled placeholder="姓名由系统同步，不可修改" />
            </a-form-item>
          </a-col>

          <a-col :xs="24" :md="12">
            <a-form-item label="性别" name="gender" :rules="[{ required: true, message: '请选择性别' }]">
              <a-select v-model="form.gender" placeholder="请选择性别">
                <a-select-option value="M">男</a-select-option>
                <a-select-option value="F">女</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>

          <a-col :xs="24" :md="12">
            <a-form-item label="电话" name="phone" :rules="[{ required: true, message: '请输入电话' }]">
              <a-input v-model="form.phone" placeholder="请输入电话" />
            </a-form-item>
          </a-col>

          <a-col :xs="24" :md="12">
            <a-form-item label="工作类型" name="worktype" :rules="[{ required: true, message: '请选择工作类型' }]">
              <a-select v-model="form.worktype" placeholder="请选择工作类型">
                <a-select-option value="物业人员">物业人员</a-select-option>
                <a-select-option value="工程人员">工程人员</a-select-option>
                <a-select-option value="安保人员">安保人员</a-select-option>
                <a-select-option value="管理人员">管理人员</a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- 照片上传 + 预览 -->
        <a-row :gutter="16">
          <a-col :xs="24" :md="12">
            <a-form-item label="照片" name="photo" :rules="[{ required: true, message: '请上传照片' }]">
              <div class="upload-row">
                <a-upload
                  accept="image/*"
                  :before-upload="beforeUpload"
                  :show-upload-list="false"
                >
                  <a-button>选择照片</a-button>
                </a-upload>
                <span v-if="photoFile" class="muted file-name">{{ photoFile.name }}</span>
              </div>

              <div v-if="photoPreviewUrl" class="preview-box">
                <img :src="photoPreviewUrl" alt="preview" class="preview-img" />
                <div class="muted preview-hint">确认是否选对图片</div>
              </div>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- 操作按钮（右侧操作区） -->
        <div class="actions">
          <a-button @click="goList" :disabled="submitting">取消</a-button>
          <a-button type="primary" @click="onSubmit" :loading="submitting">保存</a-button>
        </div>
      </a-form>
    </a-card>
  </div>
</template>

<script>
import { Form, Input, Select, Upload, Button, Row, Col, Card, message } from 'ant-design-vue'
import request from '@/utils/request'

export default {
  name: 'EmployeeEdit',
  components: {
    'a-card': Card,
    'a-form': Form,
    'a-form-item': Form.Item,
    'a-input': Input,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-upload': Upload,
    'a-button': Button,
    'a-row': Row,
    'a-col': Col
  },
  data() {
    return {
      form: { name: '', gender: '', phone: '', worktype: '' },
      photoFile: null,
      photoPreviewUrl: '',
      submitting: false
    }
  },
  beforeDestroy() {
    this.revokePreviewUrl()
  },
  created() {
    this.fetchEmployee()
  },
  methods: {
    // 返回员工列表
    goList() {
      this.$router.push({ name: 'EmployeeList' })
    },

    // 清除图片预览URL
    revokePreviewUrl() {
      if (this.photoPreviewUrl) {
        try { URL.revokeObjectURL(this.photoPreviewUrl) } catch (e) {}
        this.photoPreviewUrl = ''
      }
    },

    // 处理图片上传
    beforeUpload(file) {
      const isImage = ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)
      if (!isImage) {
        message.error('只能上传 PNG/JPG/JPEG 格式')
        return false
      }
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isLt2M) {
        message.error('图片大小需小于 2MB')
        return false
      }

      // 释放旧的图片预览
      this.revokePreviewUrl()

      this.photoFile = file
      this.photoPreviewUrl = URL.createObjectURL(file)
      return false // 阻止自动上传
    },

    // 获取雇员信息
    async fetchEmployee() {
      const id = this.$route.params.id
      try {
        const res = await request({
          url: `/employees/${id}/`,
          method: 'get'
        })
        // 假设接口返回字段与 form 相同，并含有 photo 字段 URL
        this.form.name = res.name
        this.form.gender = res.gender
        this.form.phone = res.phone
        this.form.worktype = res.worktype
        this.photoPreviewUrl = res.photo || ''
      } catch (e) {
        console.error(e)
        message.error('获取员工信息失败')
      }
    },

    // 提交更新操作
    async onSubmit() {
      if (!this.form.gender || !this.form.phone || !this.form.worktype) {
        return message.error('请补全必填项')
      }
      const phone = String(this.form.phone || '').trim()
      if (!/^\d{11}$/.test(phone)) {
        return message.error('请输入11位数字的手机号')
      }

      this.submitting = true
      const fd = new FormData()
      fd.append('gender', this.form.gender)
      fd.append('phone', phone)
      fd.append('worktype', this.form.worktype)
      if (this.photoFile) fd.append('photo', this.photoFile)

      try {
        await request({
          url: `/employees/${this.$route.params.id}/`,
          method: 'patch',
          data: fd,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        message.success('保存成功')
        this.goList()
      } catch (e) {
        console.error(e)
        message.error('保存失败')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.page {
  padding: 16px;
}

.toolbar-card,
.content-card {
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
}

.toolbar-card {
  margin-bottom: 16px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.title {
  font-size: 18px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
}

.sub {
  font-size: 12px;
}

.toolbar-actions {
  display: flex;
  gap: 8px;
}

.muted {
  color: rgba(0, 0, 0, 0.45);
}

/* 上传 + 预览 */
.upload-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.file-name {
  font-size: 12px;
}

.preview-box {
  margin-top: 10px;
  border: 1px dashed rgba(0, 0, 0, 0.15);
  border-radius: 12px;
  padding: 10px;
  display: inline-block;
}

.preview-img {
  width: 140px;
  height: 140px;
  object-fit: cover;
  border-radius: 10px;
  display: block;
}

.preview-hint {
  margin-top: 6px;
  font-size: 12px;
}

/* 操作区（不再全宽按钮） */
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
</style>
